import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import Select from 'react-select';
import {
  CreatePainelGerencial,
  DeletePainelGerencial,
  GetPainelGerencial,
  UpdatePainelGerencial,
} from './painel-gerencial.service';
import { FieldError, formControlClass } from '@/shared/components/forms/FormFieldFeedback';
import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form';
import {
  miniSuffixToUrl,
  miniUrlToSuffix,
  painelGerencialFormSchema,
  type PainelGerencialFormValues,
} from './painel-gerencial.schema';
import { toast } from 'react-toastify';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { BiPlus, BiTrash } from 'react-icons/bi';

type UsuarioOpcao = { value: number; label: string };

export default function PainelGerencialEditPage() {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  const [loaded, setLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalExcluir, setModalExcluir] = useState(false);
  const [usuariosOpcoes, setUsuariosOpcoes] = useState<UsuarioOpcao[]>([]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<PainelGerencialFormValues>({
    resolver: zodResolver(painelGerencialFormSchema) as unknown as Resolver<PainelGerencialFormValues>,
    mode: 'onTouched',
    defaultValues: {
      nome: '',
      descricao: undefined,
      areaResponsavel: '',
      url: '',
      ativo: true,
      minis: [],
      usuarioIds: [],
    },
  });

  const urlBase = watch('url');
  const minisWatch = watch('minis');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'minis',
  });

  useEffect(() => {
    Promise.all([
      GetUsuarios({ skip: 0, take: 2000 }).then((result) => {
        const lista = result?.[0] ?? [];
        setUsuariosOpcoes(
          lista
            .map((u) => ({
              value: u.id!,
              label: [u.pessoa?.nome, u.username].filter(Boolean).join(' — ') || String(u.id),
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
        );
      }),
      id > 0
        ? GetPainelGerencial(id).then((painel) => {
            reset({
              nome: painel.nome,
              descricao: painel.descricao ?? undefined,
              areaResponsavel: painel.areaResponsavel,
              url: painel.url,
              ativo: painel.ativo,
              minis: [...(painel.minis ?? [])]
                .sort((a, b) => a.ordem - b.ordem || a.id - b.id)
                .map((m) => ({
                  nome: m.nome,
                  url: miniUrlToSuffix(m.url, painel.url),
                })),
              usuarioIds: (painel.usuarios ?? []).map((u) => u.id),
            });
          })
        : Promise.resolve(),
    ])
      .then(() => setLoaded(true))
      .catch((err) => {
        toast.error(String(err));
        setLoaded(true);
      });
  }, [id, reset]);

  const onSubmit = (data: PainelGerencialFormValues) => {
    setIsLoading(true);
    const payload = {
      nome: data.nome,
      descricao: data.descricao ?? null,
      areaResponsavel: data.areaResponsavel,
      url: data.url,
      ativo: data.ativo,
      minis: data.minis.map((mini, index) => ({
        nome: mini.nome,
        url: miniSuffixToUrl(data.url, mini.url),
        ordem: index,
      })),
      usuarioIds: data.usuarioIds,
    };

    const promise =
      id > 0 ? UpdatePainelGerencial(id, payload) : CreatePainelGerencial(payload);

    promise
      .then(() => {
        toast.success(id > 0 ? 'Painel atualizado.' : 'Painel cadastrado.');
        navigate(Links.PaineisGerenciais_Admin);
      })
      .catch((err) => toast.error(String(err)))
      .finally(() => setIsLoading(false));
  };

  const handleExcluir = () => {
    setModalExcluir(false);
    DeletePainelGerencial(id)
      .then(() => {
        toast.success('Painel removido.');
        navigate(Links.PaineisGerenciais_Admin);
      })
      .catch((err) => toast.error(String(err)));
  };

  const usuariosPorId = useMemo(() => {
    const map = new Map(usuariosOpcoes.map((o) => [o.value, o]));
    return map;
  }, [usuariosOpcoes]);

  if (!loaded) return <Loading />;

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3>Painel Gerencial</h3>
        </div>
      </div>
      <hr className="pb-4" />

      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit, () =>
            toast.error('Corrija os campos destacados antes de salvar.'),
          )(event);
        }}
      >
        <div className="row">
          <div className="col-12 mb-3">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span>Informações</span>
                <div className="form-check form-switch mb-0">
                  <input
                    id="ativo"
                    type="checkbox"
                    className="form-check-input"
                    {...register('ativo')}
                  />
                  <label className="form-check-label" htmlFor="ativo">
                    Ativo
                  </label>
                </div>
              </div>
              <div className="card-body row">
                <div className="col-md-5 mb-3">
                  <label htmlFor="nome" className="form-label">
                    Nome: <span className="text-danger">*</span>
                  </label>
                  <input
                    id="nome"
                    type="text"
                    className={formControlClass(!!errors.nome)}
                    {...register('nome')}
                  />
                  <FieldError message={errors.nome?.message} />
                </div>
                <div className="col-md-7 mb-3">
                  <label htmlFor="descricao" className="form-label">
                    Descrição:
                  </label>
                  <input
                    id="descricao"
                    type="text"
                    className={formControlClass(!!errors.descricao)}
                    {...register('descricao')}
                  />
                  <FieldError message={errors.descricao?.message} />
                </div>
                <div className="col-md-5 mb-3">
                  <label htmlFor="areaResponsavel" className="form-label">
                    Área responsável: <span className="text-danger">*</span>
                  </label>
                  <input
                    id="areaResponsavel"
                    type="text"
                    className={formControlClass(!!errors.areaResponsavel)}
                    {...register('areaResponsavel')}
                  />
                  <FieldError message={errors.areaResponsavel?.message} />
                </div>
                <div className="col-md-7 mb-3">
                  <label htmlFor="url" className="form-label">
                    URL Base: <span className="text-danger">*</span>
                  </label>
                  <input
                    id="url"
                    type="url"
                    className={formControlClass(!!errors.url)}
                    placeholder="http://metabase..."
                    {...register('url')}
                  />
                  <FieldError message={errors.url?.message} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 mb-3">
            <div className="card">
              <div className="card-header">Usuários com acesso</div>
              <div className="card-body">
                <Controller
                  name="usuarioIds"
                  control={control}
                  render={({ field }) => (
                    <Select
                      isMulti
                      options={usuariosOpcoes}
                      placeholder="Selecione usuários..."
                      value={field.value
                        .map((uid) => usuariosPorId.get(uid))
                        .filter((o): o is UsuarioOpcao => !!o)}
                      onChange={(opts) =>
                        field.onChange((opts ?? []).map((o) => o.value))
                      }
                      classNamePrefix="react-select"
                    />
                  )}
                />
                <FieldError message={errors.usuarioIds?.message} />
              </div>
            </div>
          </div>

          <div className="col-12 mb-3">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span>Itens do Menu</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  title="Adicionar item"
                  aria-label="Adicionar item"
                  onClick={() => append({ nome: '', url: '/' })}
                >
                  <BiPlus size={18} />
                </button>
              </div>
              <div className="card-body">
                {fields.length === 0 ? (
                  <p className="text-muted small mb-0">
                    Nenhum item. Sem itens, o painel abre a URL base em nova aba.
                  </p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '28%' }}>Nome</th>
                          <th>Caminho</th>
                          <th style={{ width: '3rem' }} />
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field, index) => (
                          <tr key={field.id}>
                            <td>
                              <input
                                type="text"
                                className={formControlClass(!!errors.minis?.[index]?.nome)}
                                {...register(`minis.${index}.nome`)}
                              />
                              <FieldError message={errors.minis?.[index]?.nome?.message} />
                            </td>
                            <td>
                              <input
                                type="text"
                                className={formControlClass(!!errors.minis?.[index]?.url)}
                                placeholder="/collection/..."
                                title={miniSuffixToUrl(
                                  urlBase || '',
                                  minisWatch?.[index]?.url ?? '',
                                )}
                                {...register(`minis.${index}.url`)}
                              />
                              <FieldError message={errors.minis?.[index]?.url?.message} />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                title="Remover item"
                                aria-label="Remover item"
                                onClick={() => remove(index)}
                              >
                                <BiTrash size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <BotoesSalvarExcluir
          isLoading={isLoading}
          texto={btnSalvar}
          textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
          id={id}
          onExcluirClick={() => setModalExcluir(true)}
        />
      </form>

      <ModalExcluir
        show={modalExcluir}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluir(false)}
      />
    </>
  );
}
